import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    Typography
} from '@mui/material';
import { ArrowBackIos, CheckCircle, Circle, Close, RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { TextField } from 'formik-mui';
import { CARD_FORM_STEP } from '../../CreateCard';
import { CARD_TYPE } from 'helpers/constants';
import { Users } from 'lib/api';
import physicalCard from 'assets/createCards/physicalCard.png';
import virtualCard from 'assets/createCards/virtualCard.png';
import * as Yup from 'yup';
import { USER_ROLES, US_PHONE_NUMBER_REGEX, FIRST_LAST_NAME_REGEX, EMAIL_REGEX } from 'helpers/constants';
import cloneDeep from 'lodash/cloneDeep';
import { ReactComponent as GroupIcon } from 'assets/userForm/Group.svg';
import { ReactComponent as SettingsIcon } from 'assets/userForm/Settings.svg';

const USER_VALUES_TO_UPDATE = {
    firstName: 'firstName',
    lastName: 'lastName',
    jobTitle: 'jobTitle',
    department: 'department',
    id_role: 'id_role',
    phoneNumber: 'phoneNumber'
};

const validationSchema = Yup.object().shape({
    firstName: Yup.string()
        .matches(FIRST_LAST_NAME_REGEX, { message: 'No special characters allowed' })
        .max(255, 'Max. 255 characters')
        .required('Required'),
    lastName: Yup.string()
        .matches(FIRST_LAST_NAME_REGEX, { message: 'No special characters allowed' })
        .max(255, 'Max. 255 characters')
        .required('Required'),
    jobTitle: Yup.string().max(255, 'Max. 255 characters').nullable().optional(),
    department: Yup.string().max(255, 'Max. 255 characters').nullable().optional(),
    email: Yup.string().matches(EMAIL_REGEX, { message: 'please add a valid business email' }).required('Required'),
    phoneNumber: Yup.string().matches(US_PHONE_NUMBER_REGEX, { message: 'Invalid phone number' }).required('Required')
});

const CompleteUserProfile = ({ card, cardholder, setCardholder, onBackBtnClick, onStepSubmit, onCardModalClose }) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [user] = useState({
        id: cardholder.id,
        firstName: '',
        lastName: '',
        jobTitle: '',
        department: '',
        email: cardholder.email,
        phoneNumber: '',
        id_role: cardholder.id_role
    });

    const handleSubmit = async (values, actions) => {
        try {
            setIsSubmitting(true);
            await updateUser(values);
            onStepSubmit(card, actions); // Once user was completed correctly, we go to next step.
        } catch (error) {
            setIsSubmitting(false);
            if (error.response) {
                const response = error.response.data;
                if (response.type === 'Users/PhoneNumberExists') {
                    return actions.setFieldError('phoneNumber', response.message);
                }
                if (response.type === 'Users/EmailExists') {
                    return actions.setFieldError('email', response.message);
                }
                if (response.type === 'Users/ProviderError') {
                    return setErrorMessage(response.message);
                }
            } else {
                setErrorMessage('There was an error trying to add the new user to the database.');
            }
        }
    };
    const updateUser = async values => {
        const body = mapValuesToBodyModel(values);
        await Users.updateUser(user?.id, body);
        // Cardholder object must be updated in order to display right text in Card Overview Page.
        setCardholder({
            ...cardholder,
            first_name: body.firstName,
            last_name: body.lastName
        });
    };
    const mapValuesToBodyModel = values => {
        const currentUserValues = cloneDeep(values);
        const body = {};
        for (const key of Object.keys(currentUserValues)) {
            if (key in USER_VALUES_TO_UPDATE) {
                body[key] = currentUserValues[key];
            }
        }
        return body;
    };

    return (
        <Box className="create-card" sx={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                    <Box className="create-card__modal-title">Select the cardholder</Box>
                    <Box className="create-card__modal-paginator">
                        <span className="create-card__modal-page-indicator">2/4 Steps</span>
                        <Circle className="create-card__indicator-active" />
                        <Circle className="create-card__indicator-active" />
                        <Circle className="create-card__indicator-inactive" />
                        <Circle className="create-card__indicator-inactive" />
                    </Box>
                </Box>

                <Box alignSelf="start">
                    <IconButton onClick={onCardModalClose}>
                        <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                    </IconButton>
                </Box>
            </Box>
            <Grid container columnSpacing={{ md: 3 }}>
                <Grid item md={6}>
                    <Formik
                        initialValues={user}
                        enableReinitialize={true}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}>
                        {({ setFieldValue, values }) => (
                            <Form id="card-cardholder-form">
                                <Grid container rowSpacing={2} columnSpacing={2} sx={{ marginTop: '8px' }}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormLabel required className="create-card__user-input-label">
                                                First Name
                                            </FormLabel>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                name="firstName"
                                                type="text"
                                                size="small"
                                                placeholder="Enter name"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormLabel required className="create-card__user-input-label">
                                                Last Name
                                            </FormLabel>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                name="lastName"
                                                type="text"
                                                size="small"
                                                placeholder="Enter last name"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormLabel className="create-card__user-input-label">Job Title</FormLabel>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                name="jobTitle"
                                                type="text"
                                                size="small"
                                                placeholder="Enter job title"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormLabel className="create-card__user-input-label">Department</FormLabel>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                name="department"
                                                type="text"
                                                size="small"
                                                placeholder="Enter department"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required>
                                            <FormLabel className="create-card__user-input-label">Email Address</FormLabel>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                name="email"
                                                type="text"
                                                size="small"
                                                placeholder="Enter email Address"
                                                disabled={user?.id ? true : false}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth required>
                                            <FormLabel className="create-card__user-input-label">Phone Number</FormLabel>
                                            <Field
                                                component={TextField}
                                                variant="outlined"
                                                name="phoneNumber"
                                                type="text"
                                                size="small"
                                                placeholder="Enter phone number"
                                                disabled={cardholder?.id && cardholder?.phoneNumber ? true : false}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Typography>+1</Typography>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <Box marginTop="16px">
                                    <FormLabel required className="create-card__user-input-label">
                                        Select role
                                    </FormLabel>
                                    <Box
                                        className={`
                                                create-cardholder__user-role${' '}
                                                ${values.id_role === USER_ROLES.ADMIN && 'create-cardholder__user-role-checked'}
                                            `}
                                        onClick={() => setFieldValue('id_role', USER_ROLES.ADMIN)}>
                                        <Box display="flex">
                                            {values.id_role === USER_ROLES.ADMIN ? (
                                                <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                            ) : (
                                                <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                            )}
                                            <Box>
                                                <Typography component="div" className="create-cardholder__user-role-name">
                                                    Administrator
                                                </Typography>
                                                <Typography component="div" className="create-cardholder__user-role-description">
                                                    Can create and edit cards
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box alignSelf="center">
                                            <SettingsIcon />
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`
                                                create-cardholder__user-role${' '}
                                                ${
                                                    values.id_role === USER_ROLES.CARD_HOLDER &&
                                                    'create-cardholder__user-role-checked'
                                                }
                                            `}
                                        onClick={() => setFieldValue('id_role', USER_ROLES.CARD_HOLDER)}>
                                        <Box display="flex">
                                            {values.id_role === USER_ROLES.CARD_HOLDER ? (
                                                <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                            ) : (
                                                <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                            )}
                                            <Box>
                                                <Typography component="div" className="create-cardholder__user-role-name">
                                                    Cardholder
                                                </Typography>
                                                <Typography component="div" className="create-cardholder__user-role-description">
                                                    Can see and use cards in their name
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box alignSelf="center">
                                            <GroupIcon />
                                        </Box>
                                    </Box>
                                </Box>
                                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                            </Form>
                        )}
                    </Formik>
                </Grid>
                <Grid item md={6}>
                    <Box className="create-card__card-banner-container">
                        <Box className="create-card__card-image-container">
                            <img
                                className="create-card__card-image"
                                src={card.type === CARD_TYPE.VIRTUAL ? virtualCard : physicalCard}
                                alt="virtual card"
                            />
                        </Box>
                        <Box className="create-card__card-banner-benefits">
                            <Box width="300px">
                                <Box display="flex" alignItems="center" marginBottom="22px">
                                    <CheckCircle sx={{ color: '#1BABE2', marginRight: '14px' }} />
                                    <Typography component="span" className="create-card__card-banner-benefit">
                                        Works anywhere Visa is accepted
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" marginBottom="22px">
                                    <CheckCircle sx={{ color: '#1BABE2', marginRight: '14px' }} />
                                    <Typography component="span" className="create-card__card-banner-benefit">
                                        Earns Bitcoin with every transaction
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center">
                                    <CheckCircle sx={{ color: '#1BABE2', marginRight: '14px' }} />
                                    <Typography component="span" className="create-card__card-banner-benefit">
                                        Incurs zero transaction fees on Paystand Network
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <Box display="flex" justifyContent="space-between" sx={{ marginTop: '20px' }}>
                <Button variant="outlined" onClick={() => onBackBtnClick(CARD_FORM_STEP.TYPE)}>
                    <ArrowBackIos fontSize="small" />
                </Button>
                <Button variant="contained" type="submit" form="card-cardholder-form">
                    {isSubmitting ? <CircularProgress size="20px" /> : 'Update'}
                </Button>
            </Box>
        </Box>
    );
};

export default CompleteUserProfile;
